"use client";

import React from "react";
import {
  HeadlineBodyText,
  BodyText,
  HeadlineText,
} from "src/components/KinsaTypography";
import type { BillingPeriod } from "../hooks/use-paywall-state";
import { useExperimentValueSync } from "src/lib/useExperimentValue";

/** Narrower product type for the Kinsa two-product paywall tabs. */
type TabsProductType = "unlimitedRx" | "pediatricSupport";

interface ProductTabsProps {
  selectedProduct: TabsProductType;
  onProductChange: (product: TabsProductType) => void;
  rxProductTitle: string;
  pedsProductTitle: string;
  rxProductDescription: string;
  pedsProductDescription: string;
  billingPeriod: BillingPeriod;
  pedsMonthlyPrice: number;
  pedsYearlyPrice: number;
}

/**
 * Product tab switcher for Fast Prescriptions and Comprehensive Kids Care
 * Shows both products vertically with descriptions and pricing
 * Selected state shows teal border and light teal background
 * Prices are hardcoded per Figma design
 */

export function ProductTabs({
  selectedProduct,
  onProductChange,
  rxProductTitle,
  pedsProductTitle,
  rxProductDescription,
  pedsProductDescription,
  billingPeriod,
  pedsMonthlyPrice,
  pedsYearlyPrice,
}: ProductTabsProps) {
  const isUnlimitedRx = selectedProduct === "unlimitedRx";

  // Autotune experiment for badge text
  const badgeText = useExperimentValueSync(
    "promotionalbadgecopy",
    "text",
    "limited time offer"
  );

  // Hardcoded prices per Figma design
  // Fast Prescriptions: $29/month, $199/year (Normally $39 / $299)
  const displayRxPrice = billingPeriod === "yearly" ? 199 : 29;
  // Comprehensive Kids Care: driven by autotune experiment
  const displayPedsPrice =
    billingPeriod === "yearly" ? pedsYearlyPrice : pedsMonthlyPrice;

  // "Normally" anchor prices for Fast Prescriptions (LTO framing)
  const normallyRxPrice = billingPeriod === "yearly" ? 299 : 39;

  return (
    <div className="flex flex-col gap-[8px] items-start justify-center rounded-[16px] w-full overflow-visible">
      {/* Fast Prescriptions Card */}
      <button
        type="button"
        onClick={() => onProductChange("unlimitedRx")}
        className={`relative rounded-[16px] shrink-0 w-full overflow-visible transition-all duration-500 ease-out ${
          isUnlimitedRx
            ? "shadow-[0_0_0_1.5px_#00a1c5] bg-[#F1F9FC]/[0.05]"
            : "shadow-[0_0_0_0.5px_rgba(255,255,255,0.15)]"
        }`}
        aria-pressed={isUnlimitedRx}
      >
        {/* Limited Time Offer Badge */}
        <div className="absolute top-[-8px] left-[16px] z-10">
          <div
            className={`${isUnlimitedRx ? "bg-purple-800" : "bg-purple-900"} flex items-center justify-center px-[8px] py-[2px] rounded-[4px] transition-colors duration-500 ease-out`}
          >
            <span className="text-white text-[12px] font-medium uppercase tracking-[0.5px]">
              {badgeText}
            </span>
          </div>
        </div>

        <div className="box-border flex flex-col gap-[2px] items-start overflow-clip p-[16px] pt-[24px] relative rounded-[inherit] w-full">
          {/* Product Title */}
          <HeadlineBodyText className="text-white mb-0">
            {rxProductTitle}
          </HeadlineBodyText>

          {/* Product Description */}
          <BodyText className="text-slate-50 mb-0 text-left w-full">
            {rxProductDescription}
          </BodyText>

          {/* Price with "Normally" text */}
          <div className="flex items-baseline justify-between w-full">
            <div className="flex flex-col justify-end leading-[0] relative shrink-0">
              <HeadlineText className="mb-0">
                <span className="text-white">${displayRxPrice}</span>
                <span className="text-gray-500 text-[16px] font-normal">
                  /{billingPeriod === "yearly" ? "year" : "month"}{" "}
                </span>
              </HeadlineText>
            </div>
            {/* Normally price - right aligned */}
            <BodyText className="text-gray-500 mb-0">
              Normally ${normallyRxPrice}
            </BodyText>
          </div>
        </div>
      </button>

      {/* Comprehensive Kids Care Card */}
      <button
        type="button"
        onClick={() => onProductChange("pediatricSupport")}
        className={`relative rounded-[16px] shrink-0 w-full overflow-visible transition-all duration-500 ease-out ${
          !isUnlimitedRx
            ? "shadow-[0_0_0_1.5px_#00a1c5] bg-[#F1F9FC]/[0.05]"
            : "shadow-[0_0_0_0.5px_rgba(255,255,255,0.15)]"
        }`}
        aria-pressed={!isUnlimitedRx}
      >
        <div className="box-border flex flex-col gap-[2px] items-start overflow-clip p-[16px] relative rounded-[inherit] w-full">
          {/* Product Title */}
          <HeadlineBodyText className="text-white mb-0">
            {pedsProductTitle}
          </HeadlineBodyText>

          {/* Product Description */}
          <BodyText className="text-slate-50 mb-0 text-left w-full">
            {pedsProductDescription}
          </BodyText>

          {/* Price */}
          <div className="flex flex-col justify-end leading-[0] relative shrink-0">
            <HeadlineText className="mb-0">
              <span className="text-white">${displayPedsPrice}</span>
              <span className="text-gray-500 text-[16px] font-normal">
                /{billingPeriod === "yearly" ? "year" : "month"}{" "}
              </span>
            </HeadlineText>
          </div>
        </div>
      </button>
    </div>
  );
}
